import React, { useState, useEffect, useMemo } from "react";
import { modalBoxStyle } from "../styles";
import {
  Typography,
  Box,
  Modal,
  Grid,
  IconButton,
  CircularProgress,
  Alert,
  Button,
} from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { debounce } from "lodash";
import inventoryClient from "../../../clients/inventoryClient";
import ProductSelectionStep from "./components/ProductSelectionStep";
import QuantityEntryStep from "./components/QuantityEntryStep";
import { useMutation } from "react-query";

const AddProductToCompletedStockCountModal = ({
  isOpen,
  stockCountId,
  establishmentId,
  onSuccessCallback,
  handleCloseAddProductModal,
}) => {
  const [currentStep, setCurrentStep] = useState(1); // 1 = selection, 2 = quantity entry
  const [loading, toggleLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [inputValue, setInputValue] = useState(""); // Immediate input value
  const [searchString, setSearchString] = useState(""); // Debounced search string for API
  const [selectedProducts, setSelectedProducts] = useState([]); // Array of { productFormatId, displayName }
  const [productQuantities, setProductQuantities] = useState({}); // { productFormatId: quantity }

  useEffect(() => {
    if (isOpen) {
      // Reset state when modal opens
      setCurrentStep(1);
      setInputValue("");
      setSearchString("");
      setSearchResults([]);
      setSelectedProducts([]);
      setProductQuantities({});
    }
  }, [isOpen]);

  const getSearchResults = async () => {
    toggleLoading(true);
    const result = await inventoryClient.searchCompletedStockCountNotAddedProducts({
      establishmentId,
      stockCountId,
      searchString,
    });
    setSearchResults(result.results);
    toggleLoading(false);
  };

  useEffect(() => {
    if (!searchString) {
      toggleLoading(false);
      setSearchResults([]);
    } else {
      getSearchResults();
    }

    return () => debouncedSearch.cancel();
  }, [searchString]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    debouncedSearch(value);
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        setSearchString(value);
      }, 300),
    []
  );

  const handleSelectProduct = (product) => {
    setSelectedProducts((prev) => [...prev, product]);
  };

  const handleRemoveProduct = (productFormatId) => {
    setSelectedProducts((prev) =>
      prev.filter((p) => p.productFormatId !== productFormatId)
    );
    setProductQuantities((prev) => {
      const updated = { ...prev };
      delete updated[productFormatId];
      return updated;
    });
  };

  const handleNextStep = () => {
    setCurrentStep(2);
  };

  const handleBackStep = () => {
    setCurrentStep(1);
  };

  const handleQuantityChange = (productFormatId, quantity) => {
    setProductQuantities((prev) => ({
      ...prev,
      [productFormatId]: quantity,
    }));
  };

  const addProductToCompletedStockCountMutation = useMutation(
    inventoryClient.addProductToCompletedStockCount,
    {
      onSuccess: () => {
        onSuccessCallback();
        closeModal();
      },
      onError: (error) => {
        console.error("Error adding products to completed stock count:", error);
      },
    }
  );

  const handleSave = () => {
    const productsToAdd = selectedProducts.map((product) => ({
      productFormatId: product.productFormatId,
      quantityFound: parseFloat(productQuantities[product.productFormatId]),
    }));

    addProductToCompletedStockCountMutation.mutate({
      stockCountId,
      establishmentId,
      productsToAdd,
    });
  };

  const closeModal = () => {
    handleCloseAddProductModal();
  };

  const allQuantitiesEntered = selectedProducts.every(
    (product) =>
      productQuantities[product.productFormatId] &&
      parseFloat(productQuantities[product.productFormatId]) >= 0
  );

  return (
    <Modal
      open={isOpen}
      onClose={closeModal}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={modalBoxStyle}>
        {addProductToCompletedStockCountMutation?.isLoading ? (
          <Grid
            container
            direction="row"
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "200px",
            }}
          >
            <CircularProgress />
          </Grid>
        ) : (
          <>
            <Box sx={{ marginBottom: 4 }}>
              {currentStep === 2 ? (
                <>
                  <Grid
                    container
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 2,
                    }}
                  >
                    <Grid item>
                      <Button
                        startIcon={<ChevronLeftIcon />}
                        onClick={handleBackStep}
                        sx={{
                          color: (theme) => theme.palette.primary.main,
                          textTransform: "none",
                          fontSize: "16px",
                          padding: 0,
                          minWidth: "auto",
                          "&:hover": {
                            backgroundColor: "transparent",
                          },
                        }}
                      >
                        Back
                      </Button>
                    </Grid>
                    <Grid item>
                      <IconButton
                        sx={{ backgroundColor: "transparent" }}
                        onClick={closeModal}
                      >
                        <CloseOutlinedIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                  <Box>
                    <Typography variant="h4">
                      Enter Format Quantity
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Step {currentStep} of 2
                    </Typography>
                  </Box>
                </>
              ) : (
                <Grid
                  container
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Grid item>
                    <Typography variant="h4">
                      Add Products To Complete Count
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Step {currentStep} of 2
                    </Typography>
                  </Grid>
                  <Grid item>
                    <IconButton
                      sx={{ backgroundColor: "transparent" }}
                      onClick={closeModal}
                    >
                      <CloseOutlinedIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              )}
            </Box>

            {addProductToCompletedStockCountMutation.isError && (
              <Box sx={{ marginTop: 3, marginBottom: 2 }}>
                <Alert severity="error">
                  {addProductToCompletedStockCountMutation.error?.response?.data?.message ||
                    addProductToCompletedStockCountMutation.error?.message ||
                    "Failed to add products to stock count. Please try again."}
                </Alert>
              </Box>
            )}

            {currentStep === 1 ? (
              <ProductSelectionStep
                inputValue={inputValue}
                onInputChange={handleInputChange}
                searchResults={searchResults}
                loading={loading}
                selectedProducts={selectedProducts}
                onSelectProduct={handleSelectProduct}
                onRemoveProduct={handleRemoveProduct}
                onNext={handleNextStep}
              />
            ) : (
              <QuantityEntryStep
                selectedProducts={selectedProducts}
                productQuantities={productQuantities}
                onQuantityChange={handleQuantityChange}
                onSave={handleSave}
                canSave={allQuantitiesEntered}
              />
            )}
          </>
        )}
      </Box>
    </Modal>
  );
};

export default AddProductToCompletedStockCountModal;
